import {
    Channel,
    Envs,
    ErrorDisplayType,
    EventType,
    Languages,
    RestResponse,
    useChannelContext,
    useCurrentMenu,
    useEnvContext,
    useError,
    useEventBusEmit,
    useGlobalErrorStore,
    useLocale,
    useRouterHistory,
    useWebviewBridge,
} from 'scbk-services';
import _ from 'scbk-services/utils/lodash-extension.ts';
import { ErrorInfo } from '../stores/useGlobalErrorStore';

/**
 * 특정 오류 처리
 */
function useErrorHandler() {
    const { size: stackSize } = useRouterHistory();
    const { channel } = useChannelContext();
    const { getLocale } = useLocale();
    const { showErrorMessage } = useError();
    const { currentMenuId } = useCurrentMenu();
    const { doNCloseBizWebView } = useWebviewBridge();
    const { getEnv } = useEnvContext();
    const emit = useEventBusEmit(); // 이벤트 버스

    /**
     * errorCode로 특정 오류 처리
     * @param error정보
     */
    const handleErrorByCode = async (
        error: ErrorInfo,
        displayType: ErrorDisplayType,
        forceDisplayPage: boolean,
        variables?: unknown,
        context?: unknown,
        onConfirm?: () => Promise<void> | void,
        errorMenuId?: string, // 에러 페이지에서 확인 버튼 클릭 시 이동할 menuId
        errorMenuParameters?: Record<string, unknown>, // 에러 페이지에서 확인 버튼 클릭 시 이동할 menu의 parameters
        clearHistory?: boolean,
        onError?: (
            error: RestResponse.ErrorDetails,
            variables: unknown,
            context: unknown,
        ) => Promise<unknown> | void,
    ): Promise<void> => {
        const errorCode = error.code;
        const errorNextPage = error.nextPage ?? '';
        const errorNextParameter = error.nextPageParameters ?? {};

        emit(EventType.SecureKeypadClear);

        switch (errorCode) {
            case 'PRCLGN0014': // 중복로그인
                await showErrorMessage(error, () => {
                    emit(EventType.LoginNavigationRequest);
                });
                break;
            case 'PRCCMM0027': // 시스템 장애
            case 'PRCCMM0028': // 서비스 가능시간체크
            case 'PRCCMM0054': // 휴일 거래 차단
                useGlobalErrorStore.getState().setErrorInfo({
                    ...error,
                    errorMenuParameters: { ...errorNextParameter },
                    displayType: ErrorDisplayType.PAGE,
                });
                break;
            case 'PRCCMMERRCIL001': // 개인정보 노출자 제한 메뉴 접근 오류
                emit(EventType.MenuNavigationRequest, 'LGNSET002', {
                    routeData: { ...errorNextParameter },
                    historyOptions: { clearHistory: true },
                });
                break;
            case 'PRCLGN0015': // [acCheckType = 00] 비로그인 상태로 로그인 필수 메뉴 접근 제한 오류
                {
                    useGlobalErrorStore.getState().setErrorInfo({
                        ...error,
                        displayType: forceDisplayPage
                            ? ErrorDisplayType.PAGE
                            : ErrorDisplayType.CONFIRM,
                        onConfirm: () => {
                            const nextPageInfo = {
                                nextMenuId: errorMenuId,
                                nextPageMenuOptions: {
                                    routeData: errorMenuParameters ?? {},
                                },
                            };

                            emit(EventType.LoginNavigationRequest, {
                                routeData: {
                                    nextPageInfo: nextPageInfo,
                                },
                            });
                        },
                    });
                }
                break;
            case 'PRCCMM0050': // [acCheckType = 03] 전자서명 서명 대상 메뉴 접근 제한 오류(금융인증서/공동인증서 미보유)
            case 'PRCCMM0052': // [acCheckType = 05] 전자서명 상품 신규 메뉴 접근 제한 오류(공동인증서 미보유)
                {
                    useGlobalErrorStore.getState().setErrorInfo({
                        ...error,
                        displayType: forceDisplayPage
                            ? ErrorDisplayType.PAGE
                            : ErrorDisplayType.CONFIRM,
                        onConfirm: () => {
                            emit(EventType.MenuNavigationRequest, errorNextPage, {
                                replace: true,
                                routeData: errorNextParameter ?? {},
                            });
                        },
                    });
                }
                break;
            case 'PRCCMM0045': // [acCheckType = 04] 이체성거래 단말지정서비스 접근제어 오류
                useGlobalErrorStore.getState().setErrorInfo({
                    ...error,
                    displayType: forceDisplayPage
                        ? ErrorDisplayType.PAGE
                        : ErrorDisplayType.CONFIRM,
                });

                break;
            case 'PRCCMM0046': // [acCheckType = 04] 이체성거래 단말지정서비스 접근제어 오류 PC
                useGlobalErrorStore.getState().setErrorInfo({
                    ...error,
                    displayType: forceDisplayPage ? ErrorDisplayType.PAGE : ErrorDisplayType.MODAL,
                });

                break;
            case 'PRCCMM0057': // (MOTP - 기기변경) 변경된 기기에 모바일OTP 정보가 없습니다.
                if (channel === Channel.MB) {
                    useGlobalErrorStore.getState().setErrorInfo({
                        ...error,
                        displayType: forceDisplayPage
                            ? ErrorDisplayType.PAGE
                            : ErrorDisplayType.MODAL,
                        onConfirm: () => {
                            if (getLocale() === Languages.KO) {
                                emit(EventType.MenuNavigationRequest, 'CRTSPL003');
                            }
                        },
                    });
                }
                break;
            case 'PRCCMM0048': // [acCheckType=01] 보안매체 상태/이체서비스 상태에 따른 접근제어 오류
                // 보안매체 비정상(분실등록, 오류횟수 초과) / 이체서비스 비정상(장기미사용자...)
                // "모바일OTP"

                const errorMessage = error.errorMessage ?? '';

                if (channel === Channel.MB && typeof errorMessage === 'string') {
                    // MOTP 오류횟수 초과
                    if (errorMessage.indexOf('모바일OTP 인증번호를') > -1) {
                        useGlobalErrorStore.getState().setErrorInfo({
                            ...error,
                            nextPage: 'CRTSPL003', // 모바일OTP 재발급
                            displayType: forceDisplayPage
                                ? ErrorDisplayType.PAGE
                                : ErrorDisplayType.CONFIRM,
                        });
                    }
                    // OTP / 보안카드 오류횟수 초과
                    else if (
                        errorMessage.indexOf('>OTP/보안카드') > -1 ||
                        errorMessage.indexOf('>OTP/Security card') > -1
                    ) {
                        useGlobalErrorStore.getState().setErrorInfo({
                            ...error,
                            nextPage: 'CRTATH002', // 보안카드/OTP 오류해제
                            displayType: forceDisplayPage
                                ? ErrorDisplayType.PAGE
                                : ErrorDisplayType.CONFIRM,
                        });
                    } else if (errorMessage.indexOf('>이체제한을 해제할까요?') > -1) {
                        useGlobalErrorStore.getState().setErrorInfo({
                            ...error,
                            nextPage: 'TRNMGT100', // 장기미사용자 해제
                            displayType: forceDisplayPage
                                ? ErrorDisplayType.PAGE
                                : ErrorDisplayType.CONFIRM,
                        });
                    } else {
                        useGlobalErrorStore.getState().setErrorInfo({
                            ...error,
                            displayType: forceDisplayPage
                                ? ErrorDisplayType.PAGE
                                : ErrorDisplayType.MODAL,
                        });
                    }
                } else {
                    useGlobalErrorStore.getState().setErrorInfo({
                        ...error,
                        displayType: forceDisplayPage
                            ? ErrorDisplayType.PAGE
                            : ErrorDisplayType.MODAL,
                    });
                }

                break;
            case 'PRCCMM0049': // [acCheckType=02] 조회사용자가 이체성거래 접근제어 오류
            case 'PRCCMM0074': // [acCheckType=02] 해외 IP 전체 차단 사용자가 이체성거래 접근제어 오류
            case 'PRCCMM0075': // [acCheckType=02] 해외 IP 전체 특정국가 차단 사용자 이체성거래 접근제어 오류
                {
                    useGlobalErrorStore.getState().setErrorInfo({
                        ...error,
                        displayType: forceDisplayPage
                            ? ErrorDisplayType.PAGE
                            : ErrorDisplayType.MODAL,
                        onConfirm: () => {},
                    });
                }
                break;
            case 'PRCCMM0038': //암호화 세션만료
            case 'PRCCMM0039': //암호화 세션만료
                await showErrorMessage(error, function () {
                    emit(EventType.RouteBackRequest, { step: stackSize() - 1 });
                    if (
                        (Envs.DEV === getEnv() || Envs.SIT === getEnv()) &&
                        currentMenuId === 'LGNMTH000'
                    ) {
                        // close 테스트
                        doNCloseBizWebView({ type: 'v4' });
                    }
                });

                break;
            case 'PRCCMM0011':
            case 'PRCCMM0072':
                useGlobalErrorStore.getState().setErrorInfo({
                    ...error,
                    displayType: forceDisplayPage ? ErrorDisplayType.PAGE : error.displayType,
                    errorMenuId: 'BACK',
                    onConfirm,
                    clearHistory,
                });
                break;
            case 'PRCCMM0076': //암호화 세션만료
                {
                    useGlobalErrorStore.getState().setErrorInfo({
                        ...error,
                        displayType: forceDisplayPage
                            ? ErrorDisplayType.PAGE
                            : ErrorDisplayType.MODAL,
                        onConfirm: () => {
                            // HOME으로 이동
                            emit(EventType.RouteHistoryEmpty);
                        },
                    });
                }
                break;
            case 'PRCCMM0077': // 경제제재국가 접근
                useGlobalErrorStore.getState().setErrorInfo({
                    ...error,
                    displayType: ErrorDisplayType.PAGE,
                });
                break;
            case 'PRCCMM0078': //Ipinside 프로그램 미설치로 인한 접근실패
                {
                    useGlobalErrorStore.getState().setErrorInfo({
                        ...error,
                        displayType: forceDisplayPage
                            ? ErrorDisplayType.PAGE
                            : ErrorDisplayType.CONFIRM,
                        onConfirm: () => {
                            emit(EventType.ProgramInstallNavigationRequest);
                        },
                    });
                }
                break;
            // Mobile web 비수정
            // case 'PRCCMM0058': //매체접근제어 차단: 모바일뱅킹 앱에서 이용 가능한 서비스입니다. 앱 실행 안내 페이지로 이동하시겠습니까?
            // case 'PRCCMM0059': //로그인접근제어 차단 : 모바일뱅킹 앱에서 이용 가능한 서비스입니다. 앱 실행 안내 페이지로 이동하시겠습니까?
            //     // MA3CMMCMM001
            //     goMenu('', {
            //         routeData: { reqUrl: 'LGNMTH000', reqTitle: '해당 서비스' },
            //     });
            //     break;
            case '4544': {
                // OTP보정 에러처리 OTP보정페이지로 이동
                useGlobalErrorStore.getState().setErrorInfo({
                    ...error,
                    nextPage: 'CRTATH003', //모바일OTP 시간보정
                    displayType: forceDisplayPage ? ErrorDisplayType.PAGE : ErrorDisplayType.MODAL,
                    onConfirm,
                    clearHistory,
                });
                break;
            }
            case 'CIINFO_ERR':
                {
                    // 페이코 CI정보 비교 에러 시
                    useGlobalErrorStore.getState().setErrorInfo({
                        ...error,
                        displayType: forceDisplayPage
                            ? ErrorDisplayType.PAGE
                            : ErrorDisplayType.MODAL,
                        onConfirm,
                        clearHistory,
                    });
                }
                break;
            case 'INI310':
                {
                    // 금융인증서 타행 미등록 인증서로 로그인 시도시 처리
                    useGlobalErrorStore.getState().setErrorInfo({
                        ...error,
                        nextPage: 'CRTFCT002', // 타기관 금융인증서 등록
                        displayType: forceDisplayPage ? ErrorDisplayType.PAGE : displayType,
                        onConfirm,
                        clearHistory,
                    });
                }
                break;
            default:
                //정의되지 않은 기본처리
                const secuServiceYn = error?.errorPageParameters?.['SECU_SERVICE_YN']; // 보안매체 Exception시 Y
                const disorderYn = error?.errorPageParameters?.['DISORDER_YN']; // 긴급장애

                if (_.isNotNil(onError)) {
                    onError?.(error, variables, context);
                } else if (errorMenuId && secuServiceYn !== 'Y') {
                    //본거래 && 보안매체 에러가 아니면 에러페이지. (보안매체는 alert)
                    useGlobalErrorStore.getState().setErrorInfo({
                        ...error,
                        errorMenuId: errorMenuId,
                        errorMenuParameters: errorMenuParameters,
                        displayType: ErrorDisplayType.PAGE,
                        onConfirm,
                        clearHistory,
                    });
                } else if (disorderYn === 'Y') {
                    //긴급장애는 에러페이지
                    useGlobalErrorStore.getState().setErrorInfo({
                        ...error,
                        errorMenuId: errorNextPage ?? 'BACK',
                        errorMenuParameters: errorNextParameter ?? {},
                        displayType: ErrorDisplayType.PAGE,
                        onConfirm,
                        clearHistory,
                    });
                } else if (secuServiceYn === 'Y') {
                    // 보안매체 에러인 경우 Modal
                    useGlobalErrorStore.getState().setErrorInfo({
                        ...error,
                        displayType: forceDisplayPage
                            ? ErrorDisplayType.PAGE
                            : ErrorDisplayType.MODAL,
                        onConfirm,
                        clearHistory,
                    });
                } else {
                    // ******************************************
                    // 업무화면에서 에러처리 파라미터가 없는 경우 서버 에러처리 파라미터 설정
                    // ******************************************
                    useGlobalErrorStore.getState().setErrorInfo({
                        ...error,
                        displayType: forceDisplayPage ? ErrorDisplayType.PAGE : displayType,
                        onConfirm,
                        clearHistory,
                    });
                }
                break;
        }
    };

    return {
        showErrorMessage,
        handleErrorByCode,
    };
}

export { useErrorHandler };
